<template>
  <vs-popup
    :active="active"
    id="pop-up-container"
    button-close-hidden
    @update:active="$emit('update:active', $event)"
    @close="onClose()"
  >
    <div class="flex w-full items-center">
      <h3 class="w-full font-semibold text-2xl">
        {{ form.id ? 'Edit' : 'Add' }} OEE Item ({{ form.level }})
      </h3>
      <img
        class="vs-popup--close-button cursor-pointer"
        :src="require('@/assets/shipo-popup-icon-close.svg')"
        @click="onClose()"
      >
    </div>
    <hr class="border-dashed mt-4">
    <div class="w-600px mt-8 text-black">
      <div class="grid grid-cols-2 gap-4 mb-8">
        <div class="relative">
          <div class="font-semibold mb-4">
            Name
          </div>
          <vs-input
            class="w-full"
            type="text"
            placeholder="Enter Name"
            v-model="form.name"
            :color="isFieldEmpty.name ? 'danger' : ''"
          />
          <span class="text-danger text-xs">
            * Required
          </span>
        </div>
        <div
          class="grid gap-4"
          :class="[form.level !== 'L1' ? 'grid-cols-2' : 'grid-cols-1']"
        >
          <div>
            <div class="font-semibold mb-4">
              Level
            </div>
            <vs-input
              class="w-full"
              type="text"
              disabled
              v-model="form.level"
            />
          </div>
          <div v-if="form.level !== 'L1'">
            <div class="font-semibold mb-4">
              OEE Parent
            </div>
            <vs-input
              class="w-full"
              type="text"
              disabled
              v-model="form.parent_name"
            />
          </div>
        </div>
        <div>
          <div class="font-semibold mb-4">
            Code
          </div>
          <vs-input
            class="w-full"
            type="text"
            placeholder="Enter Code"
            v-model="form.code"
            :color="isFieldEmpty.code ? 'danger' : ''"
          />
          <span
            v-if="form.level === 'L2'"
            class="text-danger text-xs"
          >
            * Required
          </span>
        </div>
        <div>
          <div class="font-semibold mb-4">
            Type
          </div>
          <select-with-search
            class="w-full"
            placeholder="Select Type"
            v-model="form.type"
            :options="typeOptions"
            :is-valid="!isFieldEmpty.type"
          />
          <span
            v-if="form.level === 'L2'"
            class="text-danger text-xs"
          >
            * Required
          </span>
        </div>
        <div>
          <div class="font-semibold mb-4">
            Column As
          </div>
          <vs-input
            class="w-full"
            type="text"
            placeholder="Enter Name in Report Column"
            v-model="form.column_as"
          />
        </div>
        <div>
          <div class="font-semibold mb-4">
            Row As
          </div>
          <vs-input
            class="w-full"
            type="text"
            placeholder="Enter Name in Report Row"
            v-model="form.row_as"
          />
        </div>
        <div class="col-span-2">
          <div class="font-semibold mb-4">
            Remark
          </div>
          <vs-input
            class="w-full"
            type="text"
            placeholder="Enter Remark"
            v-model="form.remark"
          />
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <vs-button
          class="button shadow-primary font-semibold"
          type="border"
          color="primary"
          @click="onClose()"
        >
          Cancel
        </vs-button>
        <vs-button
          class="button shadow-primary font-semibold"
          color="primary"
          @click="onHandleSubmit"
        >
          {{ form.id ? 'Save' : 'Add' }} {{ form.level }} Item
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { debounce, isEmpty } from 'lodash'

import SelectWithSearch from '@/components/selectWithSearch'

const defaultForm = {
  id: null,
  parent_id: null,
  parent_name: null,
  name: null,
  level: 'L1',
  parent: null,
  code: null,
  type: null,
  column_as: null,
  row_as: null,
  remark: null
}

export default {
  components: { SelectWithSearch },
  props: {
    active: {
      type: Boolean,
      default: () => false
    },
    data: {
      type: Object,
      default: () => defaultForm
    }
  },
  watch: {
    data: {
      handler(val) {
        this.form = {
          ...defaultForm,
          ...val
        }
      }
    },
    isLoading: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.$vs.loading({
            type: 'radius',
            color: 'primary',
            container: '#pop-up-container',
            scale: 0.5
          })

          return
        }

        this.$vs.loading.close('#pop-up-container > .con-vs-loading')
      }
    }
  },
  data() {
    return {
      form: defaultForm,
      typeOptions: [
        { text: 'Operation', value: 'Operation' },
        { text: 'Downtime', value: 'Downtime' },
        { text: 'Delay', value: 'Delay' }
      ],
      isLoading: false,
      isFieldEmpty: {
        name: false,
        code: false,
        type: false
      }
    }
  },
  methods: {
    fetch: debounce(function () {
      this.$emit('fetch')
    }, 500),
    async onHandleSubmit() {
      this.isLoading = true

      try {
        this.isFieldEmpty.name = isEmpty(this.form.name)
        this.isFieldEmpty.code = this.form.level === 'L2' && isEmpty(this.form.code)
        this.isFieldEmpty.type = this.form.level === 'L2' && isEmpty(this.form.type)
        
        const isNameUnique = this.checkNameUniqueness()
        const isCodeUnique = this.checkCodeUniqueness()
        const isValid = !this.isFieldEmpty.name && !this.isFieldEmpty.code && !this.isFieldEmpty.type && isNameUnique && isCodeUnique

        if (isValid) {
          const payload = {
            id: this.form.id,
            form: {
              name: this.form.name,
              level: this.form.level,
              parent_id: this.form.parent_id,
              code: this.form.code,
              type: this.form.type,
              column_as: this.form.column_as,
              row_as: this.form.row_as,
              remark: this.form.remark
            }
          }

          if (this.form.id) {
            await this.$store.dispatch('OEEItemsManagement/putOEEItem', payload)
            this.showSuccessNotification(this, `OEE Item ${this.form.level} successfully updated.`)
          } else {
            await this.$store.dispatch('OEEItemsManagement/postOEEItem', payload.form)
            this.showSuccessNotification(this, `OEE Item ${this.form.level} successfully added.`)
          }

          this.fetch()
          const onCloseDebounce = debounce(() => {
            this.onClose()
          }, 500)
          onCloseDebounce()
        } else {
          this.isLoading = false
          if (this.isFieldEmpty.name) {
            this.showFailedNotification(this, `Field Name cannot be empty`, 'Error')
          }
          if (this.isFieldEmpty.code) {
            this.showFailedNotification(this, `Field Code cannot be empty`, 'Error')
          }
          if (this.isFieldEmpty.type) {
            this.showFailedNotification(this, `Field Type cannot be empty`, 'Error')
          }
          if (!this.checkNameUniqueness()) {
            this.showFailedNotification(this, `Name "${this.form.name}" already exists under this parent. Please use a different name.`, 'Error')
          }
          if (!this.checkCodeUniqueness()) {
            this.showFailedNotification(this, `Code "${this.form.code}" already exists. Please use a different code.`, 'Error')
          }
        }
      } catch (error) {
        this.isLoading = false
        const message = error?.errors?.[0]?.[0]?.message || error?.message || 'Failed to save changes, please try again'

        this.showFailedNotification(this, message, 'Error')
      }
    },
    checkNameUniqueness() {
      if (this.form.level === 'L1' || !this.form.name) {
        return true
      }

      const allItems = this.$store.getters['OEEItemsManagement/OEEItemsManagement'] || []
      
      const flattenedItems = this.flattenOEEItems(allItems)
      
      const sameParentItems = flattenedItems.filter(item => 
        item.parent_id === this.form.parent_id &&
        item.id !== this.form.id
      )
      
      const isDuplicate = sameParentItems.some(item => 
        item.name && item.name.toLowerCase() === this.form.name.toLowerCase()
      )
      
      return !isDuplicate
    },
    checkCodeUniqueness() {
      if (!this.form.code) {
        return true
      }

      const allItems = this.$store.getters['OEEItemsManagement/OEEItemsManagement'] || []
      
      const flattenedItems = this.flattenOEEItems(allItems)
      
      const isDuplicate = flattenedItems.some(item => 
        item.code && 
        item.code.toLowerCase() === this.form.code.toLowerCase() &&
        item.id !== this.form.id
      )
      
      return !isDuplicate
    },
    flattenOEEItems(items) {
      const result = []
      
      const flatten = (itemList) => {
        if (!Array.isArray(itemList)) return
        
        itemList.forEach(item => {
          result.push(item)
          if (item.children && Array.isArray(item.children)) {
            flatten(item.children)
          }
        })
      }
      
      flatten(items)
      return result
    },
    onClose() {
      this.isLoading = false
      this.isFieldEmpty = {
        name: false,
        code: false,
        type: false
      }
      this.$emit('update:active', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.w-600px {
  width: 600px;
}

/deep/.vs-popup {
  @apply w-auto;
  @apply rounded-2xl;
  min-width: 30rem;

  &--title {
    @apply hidden;
  }
}
</style>